<template>
  <div :class="$style.mapWrapper">
    <div
      ref="containerElement"
      :class="[
        $style.map,
        'checkoutOSMap',
        {
          checkoutOSMapVertical: !isHorizontal,
          [$style.mapHorizontal]: isHorizontal,
        },
      ]"
    >
      <div v-pre data-aw-checkout-osm-map="map-container" />
    </div>
    <aw-spinner
      v-if="isLoading"
      :class="$style.spinner"
    />
  </div>
</template>

<script setup>
  import { onMounted, onUnmounted, ref, useTemplateRef, useCssModule, watch, computed } from 'vue';
  import { useNuxtApp, useRuntimeConfig } from 'nuxt/app';
  import { useMapLogos } from '~~/common/utils/mapLogo';
  import AwSpinner from '~~/common/components/Common/AwSpinner.vue';
  import { getOSMConfig } from '~~/shop/app/config/MapConfig';

  const MILLISEC_TIMEOUT = 800;

  const {
    $awt,
    $awRuntimeConfig,
  } = useNuxtApp();
  const $style = useCssModule();

  const props = defineProps({
    isHorizontal: {
      type: Boolean,
      default: false,
    },
    allPoints: {
      type: Array,
      required: true,
    },
    allPointsHiddenState: {
      type: Uint8Array,
      required: true,
    },
    selectedPoint: {
      type: Object,
      default: null,
    },
    disabledPoints: {
      type: Set,
      default: () => [],
    },
  });

  const emit = defineEmits(['select-point', 'visible-points']);

  const mapCenter = ref($awRuntimeConfig.mapAreaBounds);
  const containerElement = useTemplateRef('containerElement');

  let map = null;
  const leaflet = await import('leaflet');

  /* dpMarkers shouldn't be ref because then it would cost 700ms to mutate it */
  let dpMarkers = null;
  let selectedMarker = null;
  let idTimeoutVisiblePointsChange = null;

  const pointsInsideMapBounds = computed(() => {
    const vPoints = new Uint8Array(props.allPoints.length);
    if (mapCenter.value?.length !== 2) {
      return vPoints;
    }
    const lat0 = mapCenter.value[0].latitude;
    const long0 = mapCenter.value[0].longitude;
    const lat1 = mapCenter.value[1].latitude;
    const long1 = mapCenter.value[1].longitude;
    const aPoints = props.allPoints;
    for (let idx = 0; idx < vPoints.length; idx++) {
      const point = aPoints[idx];
      const coords = point.address.coordinates;
      if (!coords) {
        vPoints[idx] = 0;
      }
      const {
        latitude,
        longitude,
      } = coords;
      vPoints[idx] = (
        lat0 < latitude
        && long0 < longitude
        && lat1 > latitude
        && long1 > longitude
      ) ? 1 : 0;
    }
    return vPoints;
  });
  const osmCreationQueue0 = ref(null);
  const osmCreationQueue1 = ref(null);
  const isLoading = computed(() => (osmCreationQueue0.value || osmCreationQueue1.value));

  let resizeObserver = null;

  onMounted(async () => {
    let isAdded = false;
    let osmAddedSuccessfully = null;
    watch(containerElement, (newVal) => {
      if (containerElement.value.getBoundingClientRect().width) {
        isAdded = true;
        osmAddedSuccessfully = addToOsmQueue({ isMapRemoved: false });
      }
      if (newVal && !resizeObserver) {
        resizeObserver = new ResizeObserver(async (entries) => {
          if (map) {
            await osmAddedSuccessfully;
            map.invalidateSize();
          }
          const lastEntry = entries.slice(-1)[0];
          if (!isAdded && lastEntry.borderBoxSize.slice(-1)[0].inlineSize) {
            isAdded = true;
            osmAddedSuccessfully = addToOsmQueue({ isMapRemoved: false });
          }
        });
        resizeObserver.observe(newVal);
      } else if (!newVal && resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    }, { immediate: true });
  });
  onUnmounted(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  });

  async function addToOsmQueue ({ isMapRemoved }) {
    const queuedPromise = createQueuedPromise();
    osmCreationQueue1.value = queuedPromise;

    async function createQueuedPromise () {
      await osmCreationQueue0.value;
      if (osmCreationQueue1.value === queuedPromise) {
        osmCreationQueue0.value = queuedPromise;
        osmCreationQueue1.value = null;
        if (isMapRemoved) {
          map.remove();
        }
        await createOSM();
        if (osmCreationQueue0.value === queuedPromise) {
          osmCreationQueue0.value = null;
        }
      }
    }
  }

  const {
    getMarker,
    getLeafletPin,
  } = useMapLogos();

  async function createOSM () {
    map = (new leaflet.Map(containerElement.value.children[0])
      .fitBounds(new leaflet.LatLngBounds(
        latLng(mapCenter.value[0]),
        latLng(mapCenter.value[1]),
      ))
    );

    (new leaflet.TileLayer(...getOSMConfig(useRuntimeConfig()).tileLayer)
      .addTo(map)
    );
    map.zoomControl.remove();

    leaflet.control.zoom({
      zoomInTitle: $awt('aw.map.zoom_control.zoom_in_title'),
      zoomOutTitle: $awt('aw.map.zoom_control.zoom_out_title'),
      position: 'bottomright',
      zoomInText: `<span aria-hidden="true" class="${$style.zoomIcon}" ><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
        '<path d="M2 8L14 8M8 2L8 14" stroke="#433F32" stroke-linecap="round" stroke-linejoin="round"/></svg></span>`,
      zoomOutText: `<span aria-hidden="true" class="${$style.zoomIcon}"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
        '<path d="M2 8L14 8" stroke="#433F32" stroke-linecap="round" stroke-linejoin="round"/></svg></span>`,
    }).addTo(map);

    dpMarkers = await createMarkers();
    await clusterMap();
    map.fitBounds(new leaflet.LatLngBounds(
      latLng(mapCenter.value[0]),
      latLng(mapCenter.value[1]),
    ));
    // map.setZoom(map.getZoom());

    map.on('click', function () {
      if (selectedMarker?.dp) {
        resetSelectedMarkerIcon();
        selectedMarker = null;
        emit('select-point', null);
      }
    });

    onBoundsChanged();
  }

  async function createMarkers () {
    clearTimeout(idTimeoutVisiblePointsChange);
    idTimeoutVisiblePointsChange = setTimeout(() => {
      emit('visible-points', pointsInsideMapBounds.value);
    }, MILLISEC_TIMEOUT);

    const storeList = (props.allPoints
      .filter((_point, idx) => !props.allPointsHiddenState[idx])
    );
    return Object.fromEntries(storeList.map((dp) => {
      const m = new leaflet.Marker(latLng(dp.address.coordinates || {
        latitude: 0,
        longitude: 0,
      }), {
        icon: new leaflet.Icon(
          getLeafletPin(getMarker(
            {
              isSelected: false,
              isDisabled: false,
            },
            dp,
          )),
        ),
        title: dp.name,
      });
      m.on('click', clickHandler.bind(this, dp));
      return [dp.id, m];
    }));
  }

  function clickHandler (dp, event) {
    if (!props.disabledPoints.has(dp.id)) {
      if (selectedMarker?.dp) {
        resetSelectedMarkerIcon();
      }

      selectedMarker = {
        target: event.target,
        dp,
      };
      event.target.setIcon(new leaflet.Icon(
        getLeafletPin(getMarker(
          {
            isSelected: true,
            isDisabled: false,
          },
          dp,
        )),
      ));
      emit('select-point', dp);
      onBoundsChanged();
      map.setView(latLng(dp.address.coordinates), 42);
    }
  }

  function resetSelectedMarkerIcon () {
    selectedMarker.target.setIcon(
      new leaflet.Icon(
        getLeafletPin(getMarker(
          {
            isSelected: false,
            isDisabled: false,
          },
          selectedMarker.dp,
        )),
      ),
    );
  }

  function latLng ({
    latitude,
    longitude,
  }) {
    return new leaflet.LatLng(parseFloat(latitude), parseFloat(longitude));
  }

  function onBoundsChanged () {
    let idTimeout = null;
    const MILLISEC_TIMEOUT_LOW = 500;
    const handler = () => {
      clearTimeout(idTimeout);
      idTimeout = setTimeout(() => {
        const bounds = map.getBounds();
        const [sw, ne] = [bounds.getSouthWest(), bounds.getNorthEast()];

        mapCenter.value = [sw, ne].map(c => ({
          latitude: c.lat,
          longitude: c.lng,
        }));
      }, MILLISEC_TIMEOUT_LOW);
    };
    map.on('moveend', handler);
  }

  async function clusterMap () {
    const { MarkerClusterGroup } = await import('leaflet.markercluster');
    const mcg = new MarkerClusterGroup({
      iconCreateFunction: (cluster) => {
        return new leaflet.DivIcon({
          html: cluster.getChildCount(),
          className: $style.mapMarkerClustererCustom,
        });
      },
    });
    for (const m in dpMarkers) {
      mcg.addLayer(dpMarkers[m]);
    }
    map.addLayer(mcg);
  }

  function getPoint (id) {
    return props.allPoints.find(p => p.id === id);
  }

  watch(mapCenter, () => {
    const bounds = new leaflet.LatLngBounds(latLng(mapCenter.value[0]), latLng(mapCenter.value[1]));
    if (!bounds.equals(map.getBounds())) {
      map.fitBounds(bounds);
    }
    clearTimeout(idTimeoutVisiblePointsChange);
    idTimeoutVisiblePointsChange = setTimeout(() => {
      emit('visible-points', pointsInsideMapBounds.value);
    }, MILLISEC_TIMEOUT);
  });

  watch(() => props.selectedPoint, (newValue, oldValue) => {
    if (newValue !== oldValue) {
      const mark = dpMarkers[newValue?.id];
      if (mark) {
        mark.fire('click');
      }
    }
  });

  watch(() => props.disabledPoints, (newValue, oldValue) => {
    for (const id in dpMarkers) {
      const isNew = newValue.has(id);
      if (isNew || oldValue.has(id)) {
        const dp = dpMarkers[id];
        dp.setIcon(new leaflet.Icon(
          getLeafletPin(getMarker(
            {
              isSelected: false,
              isDisabled: isNew,
            },
            getPoint(id),
          )),
        ));
      }
    }
  }, { deep: true });

  watch(
    [
      () => props.allPoints,
      () => props.allPointsHiddenState,
    ],
    changeMap,
  );

  async function changeMap () {
    await addToOsmQueue({ isMapRemoved: true });
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.map {
  width: 100%;
  height: 100%;
  border-radius: 0 16px 16px 0;
  background: grey;

  [data-aw-checkout-osm-map="map-container"] {
    width: 100%;
    height: 100%;
  }

  &Horizontal {
    border-radius: 16px 16px 0 0;
  }

  &Wrapper {
    position: relative;
    width: 100%;
    height: 100%;
  }
}

.mapMarkerClustererCustom {
  font-size: 14px;
  font-weight: $font-weight-bold-v2;
  line-height: 20px;

  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px !important;
  height: 40px !important;
  padding: 8px;
  text-align: center;
  color: $color-text-inverse;
  border: 2px solid $color-background-4;
  border-radius: 24px;
  background: $color-olive-750;
}

.zoomIcon {
  display: block;
  width: 16px !important;
  height: 16px !important;

  svg {
    width: 16px;
    height: 16px;
  }

}

.spinner {
  position: absolute;
  z-index: 1000;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  margin: auto;
  background: rgba($color-seashell, 0.5);
}
</style>
